import React, { useState, useRef, useContext } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { MyContext } from "../../MyContextApi";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TablePagination from "@mui/material/TablePagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMaximize, faMinimize } from "@fortawesome/free-solid-svg-icons";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#1AACAC",
    color: theme.palette.common.white,
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "1rem",
    padding: theme.spacing(1.2),
    lineHeight: "1.2rem",
    border: "none", // Reset all borders
    borderRight: `1px solid ${theme.palette.divider}`, // Only add right border
    "&:last-child": {
      borderRight: "none", // Remove right border from last column
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    backgroundColor: theme.palette.common.white,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    padding: theme.spacing(1.2),
    border: "none", // Reset all bordersc
    borderRight: `1px solid ${theme.palette.divider}`, // Only add right border
    "&:last-child": {
      borderRight: "none", // Remove right border from last column
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "rgba(249, 84, 84, 0.04)",
    transition: "background-color 0.2s ease",
  },
}));

function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange, paginatedData } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const disableButtons = paginatedData.length === 0;

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={disableButtons || page === 0}
        aria-label="first page"
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={disableButtons || page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={disableButtons || page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={disableButtons || page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

function ImageAnalysisTable({ analysisData = [], tableRef }) {
  const { navigationCount, setNavigationCount } = useContext(MyContext);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const tableContainerRef = useRef();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      tableContainerRef.current.requestFullscreen();
      setIsFullscreen(isFullscreen);
    } else if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  };

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = Array.isArray(analysisData)
    ? analysisData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];
  //
  return (
    <Box
      sx={{
        width: isFullscreen ? "100vw" : "auto",
        height: isFullscreen ? "100vh" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "end",
        overflow: "hidden",
        position: "relative",
        zIndex: 0,
        borderRadius: "15px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.25)", // Added box shadow
        // marginTop: "30px",
      }}
    >
      <TableContainer
        component={Paper}
        className="custom-table-container"
        ref={tableContainerRef}
      >
        <Table
          stickyHeader
          sx={{ minWidth: 1200, tableLayout: "fixed" }}
          aria-label="customized table"
          ref={tableRef}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell
                sx={{
                  width: "40px",
                  textAlign: "left",
                  whiteSpace: "normal",
                }}
              >
                S.No
              </StyledTableCell>
              {/* <StyledTableCell>Timestamp</StyledTableCell> */}
              <StyledTableCell>Date</StyledTableCell>
              <StyledTableCell
                sx={{
                  width: "500px",
                  textAlign: "left",
                  whiteSpace: "normal",
                }}
              >
                Image
              </StyledTableCell>

              <StyledTableCell
                sx={{
                  width: "400px",
                  textAlign: "left",
                  whiteSpace: "normal",
                }}
              >
                {" "}
                categories{" "}
              </StyledTableCell>

              <StyledTableCell
                sx={{
                  width: "350px",
                  textAlign: "left",
                  whiteSpace: "normal",
                }}
              >
                Extracted Text
              </StyledTableCell>

              {/* <StyledTableCell>Confidence</StyledTableCell> */}
              {/* <StyledTableCell>Text Weight</StyledTableCell> */}
              {/* <StyledTableCell>Context</StyledTableCell> */}
              {/* <StyledTableCell>Obscenity</StyledTableCell> */}
              {/* <StyledTableCell>Probabilities</StyledTableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData &&
              paginatedData.map((data, index) => {
                const formattedDateTime =
                  data && data._id.date
                    ? data._id.date
                      .replace("T", " ") // Replacing the 'T' separator between date and time
                      .split(".")[0] // Removing the fractional seconds and the timezone part
                      .replace(" ", " / ") // Replacing space between date and time with a '/'
                    : "N/A";

                const {
                  _id,
                  image_url,
                  text,
                  category,
                  lang,
                  languageCode,
                  languageConfidence,
                  violation,
                  textWeight,
                  contextType,
                  isObscene,
                  probabilities,
                } = data;

                return (
                  <StyledTableRow key={index}>
                    <StyledTableCell>
                      {page * rowsPerPage + index + 1}
                    </StyledTableCell>
                    {/* <StyledTableCell>{_id.timestamp || "N/A"}</StyledTableCell> */}
                    <StyledTableCell>
                      {formattedDateTime || "N/A"}
                    </StyledTableCell>

                    <StyledTableCell sx={{ textAlign: "center" }}>
                      <img
                        src={image_url}
                        alt="Image"
                        style={{
                          maxWidth: "100%", // Allow the image to grow dynamically
                          height: "auto", // Maintain aspect ratio
                          objectFit: "contain", // Ensure it fits within the cell
                          cursor: "pointer", // Change cursor to indicate it's clickable
                        }}
                        onClick={() => window.open(image_url, "_blank")} // Open image in new tab
                      />
                    </StyledTableCell>

                    <StyledTableCell>
                      {category?.categories ? (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {

                            (Array.isArray(category?.categories)
                              ? category.categories
                              : (category?.categories || "").split(",")
                            )

                              .map((cat, idx) => {
                                // Array of different colors for variety
                                const colors = [
                                  "#FF6B6B", // Red
                                  "#4ECDC4", // Teal
                                  "#FFD93D", // Yellow
                                  "#A78BFA", // Purple
                                  "#FB923C", // Orange
                                  "#34D399", // Green
                                  "#F472B6", // Pink
                                  "#60A5FA", // Blue
                                ];
                                const bgColor = colors[idx % colors.length];

                                return (
                                  <Box
                                    key={idx}
                                    sx={{
                                      backgroundColor: bgColor,
                                      color:
                                        bgColor === "#FFD93D" ? "#333" : "white",
                                      padding: "4px 12px",
                                      borderRadius: "16px",
                                      fontSize: "0.85rem",
                                      fontWeight: "500",
                                      display: "inline-block",
                                      boxShadow: `0 2px 4px ${bgColor}40`,
                                    }}
                                  >
                                    {typeof cat === "string" ? cat.trim() : cat}
                                  </Box>
                                );
                              })}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            backgroundColor: "#E8E8E8",
                            color: "#666",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            fontSize: "0.85rem",
                            fontWeight: "500",
                            display: "inline-block",
                          }}
                        >
                          N/A
                        </Box>
                      )}
                    </StyledTableCell>

                    <StyledTableCell
                      sx={{
                        width: "400px",
                        textAlign: "left",
                        whiteSpace: "normal",
                      }}
                    >
                      {truncateText(lang?.extracted_text || "N/A", 400)}
                    </StyledTableCell>

                    {/* <StyledTableCell>
                    {languageConfidence || "N/A"}%
                  </StyledTableCell> */}
                    {/* <StyledTableCell>{textWeight || "N/A"}</StyledTableCell> */}
                    {/* <StyledTableCell>{contextType || "N/A"}</StyledTableCell> */}
                    {/* <StyledTableCell>{isObscene || "N/A"}</StyledTableCell> */}
                    {/* <StyledTableCell>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Label</StyledTableCell>
                          <StyledTableCell>Probability</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {probabilities.map((prob, idx) => (
                          <StyledTableRow key={idx}>
                            <StyledTableCell>{prob.label}</StyledTableCell>
                            <StyledTableCell>
                              {prob.probability}
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </StyledTableCell> */}
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          outline: "none",
          backgroundColor: "white",
          borderBottomRightRadius: "15px",
          borderBottomLeftRadius: "15px",
        }}
      >
        {" "}
        <Button onClick={toggleFullscreen}>
          {isFullscreen ? (
            <FontAwesomeIcon icon={faMinimize} fontSize={"20px"} />
          ) : (
            <FontAwesomeIcon icon={faMaximize} fontSize={"20px"} />
          )}
        </Button>
        <TablePagination
          component="div"
          count={Array.isArray(analysisData) ? analysisData.length : 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
}

export default ImageAnalysisTable;
